import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  Camera,
  Check,
  ChevronDown,
  ExternalLink,
  FolderOpen,
  ImageOff,
  KeyRound,
  LifeBuoy,
  Link2,
  PauseCircle,
  Radio,
  Layers,
  Users,
  Zap,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import NotFound from "@/pages/NotFound";
import { useHostname } from "@/lib/ssr";
import { isLiveUploadingEnabled } from "@/lib/liveUploadingFlag";
import {
  CLOUDTETHER_URL,
  LIVE_UPLOADING_HELP_PATH,
  LIVE_UPLOADING_PATH,
  SHOTS,
  type Shot as ShotData,
  useLiveUploadingCopy,
} from "@/content/liveUploading";

// "Upload live from your camera" at photohawk.com/live-uploading: shooting tethered with CloudTether, each JPEG
// delivered straight into a Photohawk gallery (CloudTether's partner API, photohawk-serverless
// docs/cloudtether-api.md), with CloudTether's own screens. Staging only until LIVE_UPLOADING_IN_PROD
// (src/lib/liveUploadingFlag.ts). Copy: src/content/liveUploading/copy.<lang>.ts. Setup and troubleshooting in
// full: the help article /help/articles/upload-live-with-cloudtether.

const CANONICAL = `https://photohawk.com${LIVE_UPLOADING_PATH}`;
/** CloudTether × Photohawk: the live shoot (scripts: CloudTether's marketing composite, 1200×630). */
const OG_IMAGE = "https://photohawk.com/images/og/live-uploading.jpg";

const STEP_ICONS: Record<string, LucideIcon> = { camera: Camera, connect: Link2, choose: FolderOpen, shoot: Radio };
const TROUBLE_ICONS: Record<string, LucideIcon> = { reconnect: KeyRound, missing: ImageOff, gallery: FolderOpen, paused: PauseCircle };

/** Copy markup: «Label» → bold (a button or screen as CloudTether or Photohawk shows it), `text` → monospace. */
const Rich = ({ text }: { text: string }) => (
  <>
    {text.split(/(«[^»]+»|`[^`]+`)/).map((part, i) => {
      if (part.startsWith("«") && part.endsWith("»")) return <strong key={i} className="font-semibold text-foreground">{part.slice(1, -1)}</strong>;
      if (part.startsWith("`") && part.endsWith("`"))
        return <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] text-foreground">{part.slice(1, -1)}</code>;
      return <Fragment key={i}>{part}</Fragment>;
    })}
  </>
);

const IconTile = ({ icon: Icon }: { icon: LucideIcon }) => (
  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
    <Icon size={18} className="text-hawk-orange" aria-hidden="true" />
  </div>
);

const Ticks = ({ items }: { items: { id: string; text: string }[] }) => (
  <ul className="space-y-2.5">
    {items.map((p) => (
      <li key={p.id} className="flex gap-2.5 text-muted-foreground">
        <Check size={18} className="mt-0.5 flex-shrink-0 text-hawk-orange" aria-hidden="true" />
        <span><Rich text={p.text} /></span>
      </li>
    ))}
  </ul>
);

/** One of CloudTether's phone screens: AVIF / WebP at 320 and 640 px, a PNG fallback, lazy-loaded below the fold. */
const Shot = ({ shot, alt, sizes, eager = false }: { shot: ShotData; alt: string; sizes: string; eager?: boolean }) => (
  <picture>
    <source type="image/avif" srcSet={shot.avif} sizes={sizes} />
    <source type="image/webp" srcSet={shot.webp} sizes={sizes} />
    <img
      src={shot.png}
      width={shot.width}
      height={shot.height}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      data-screenshot={shot.id}
      className="block h-auto w-full rounded-[1.75rem] border border-border bg-white shadow-xl"
    />
  </picture>
);

const Section = ({ children, alt = false, id, labelledBy }: { children: ReactNode; alt?: boolean; id?: string; labelledBy: string }) => (
  <section id={id} className={`scroll-mt-24 py-16 md:py-20 ${alt ? "bg-secondary/30" : ""}`} aria-labelledby={labelledBy}>
    {children}
  </section>
);

const LiveUploading = () => {
  const host = useHostname();
  const { copy } = useLiveUploadingCopy();
  if (!isLiveUploadingEnabled(host)) return <NotFound />;
  const { hero, steps, shots, landing, shoot, files, roles, trouble, faq, cta } = copy;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={copy.seo.title} />
        <meta property="og:description" content={copy.seo.description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:title" content={copy.seo.title} />
        <meta name="twitter:description" content={copy.seo.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero, with the phone screenshot */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white md:pb-20">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-4 md:grid-cols-[1fr_auto] md:gap-14">
            <div>
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-3 py-1 text-sm font-bold uppercase tracking-wide text-white shadow-glow">
                  <Radio size={14} aria-hidden="true" />
                  {hero.badge}
                </span>
                <span className="text-sm font-medium text-white/80">{hero.eyebrow}</span>
              </div>
              <h1 className="mb-5 text-4xl font-display font-bold md:text-5xl">{hero.title}</h1>
              <p className="mb-8 max-w-2xl text-lg text-white/80 md:text-xl">{hero.subtitle}</p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button variant="hero" size="lg" asChild>
                  <a href={CLOUDTETHER_URL} target="_blank" rel="noopener noreferrer">
                    {hero.primary}
                    <ExternalLink />
                  </a>
                </Button>
                <Button variant="heroOutline" size="lg" asChild>
                  <Link to={LIVE_UPLOADING_HELP_PATH}>
                    {hero.secondary}
                    <ArrowRight />
                  </Link>
                </Button>
              </div>
              <p className="mt-4 text-sm text-white/60">{hero.note}</p>
            </div>
            <div className="mx-auto w-full max-w-[260px]">
              <Shot shot={SHOTS["live-shoot"]} alt={shots.heroAlt} sizes="260px" eager />
            </div>
          </div>
        </section>

        {/* Four steps */}
        <Section id="how" alt labelledBy="steps-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <h2 id="steps-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{steps.heading}</h2>
              <p className="text-muted-foreground">{steps.subtitle}</p>
            </div>
            <ol className="grid gap-4 md:grid-cols-4 md:gap-6">
              {steps.items.map((s, i) => {
                const Icon = STEP_ICONS[s.id] ?? Check;
                return (
                  <li key={s.id} className="relative flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm md:flex-col md:gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                        <Icon size={22} className="text-white" aria-hidden="true" />
                      </div>
                      <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-hawk-navy text-xs font-bold text-white" aria-hidden="true">
                        {i + 1}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="mb-1 font-semibold"><span className="sr-only">{i + 1}. </span>{s.title}</h3>
                      <p className="text-sm text-muted-foreground"><Rich text={s.body} /></p>
                    </div>
                    {i < steps.items.length - 1 && (
                      <ArrowRight size={20} className="absolute -right-[19px] top-11 z-10 hidden text-hawk-orange md:block" aria-hidden="true" />
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </Section>

        {/* Screenshots: CloudTether on the phone, then the gallery */}
        <Section labelledBy="shots-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <h2 id="shots-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{shots.heading}</h2>
              <p className="text-muted-foreground">{shots.subtitle}</p>
            </div>
            <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {shots.items.map((item) => (
                <figure key={item.id} className="mx-auto w-full max-w-[240px]">
                  <Shot shot={SHOTS[item.id as ShotData["id"]]} alt={item.alt} sizes="240px" />
                  <figcaption className="mt-3 text-center text-sm text-muted-foreground">{item.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Section>

        {/* What Photohawk does with each photo */}
        <Section alt labelledBy="landing-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="mb-4 flex items-center gap-3">
              <IconTile icon={Zap} />
              <h2 id="landing-heading" className="text-3xl font-display font-bold md:text-4xl">{landing.heading}</h2>
            </div>
            <p className="mb-5 text-muted-foreground">{landing.body}</p>
            <Ticks items={landing.points} />
          </div>
        </Section>

        {/* A busy shoot: several cameras, counts, pocket mode, copies, other destinations */}
        <Section labelledBy="shoot-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="mb-4 flex items-center gap-3">
              <IconTile icon={Layers} />
              <h2 id="shoot-heading" className="text-3xl font-display font-bold md:text-4xl">{shoot.heading}</h2>
            </div>
            <p className="mb-5 text-muted-foreground">{shoot.body}</p>
            <Ticks items={shoot.points} />
          </div>
        </Section>

        {/* File types, and roles */}
        <Section alt labelledBy="files-heading">
          <div className="container mx-auto grid max-w-5xl gap-10 px-4 md:grid-cols-2">
            <div>
              <h2 id="files-heading" className="mb-2 text-2xl font-display font-bold md:text-3xl">{files.heading}</h2>
              <p className="mb-5 text-muted-foreground">{files.subtitle}</p>
              <dl className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                {files.rows.map((r) => (
                  <div key={r.id} className="grid gap-1 px-5 py-3.5 sm:grid-cols-[8rem_1fr] sm:gap-4">
                    <dt className="text-sm font-semibold">{r.label}</dt>
                    <dd className="min-w-0 text-sm text-muted-foreground"><Rich text={r.value} /></dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <div className="mb-5 flex items-center gap-3">
                <IconTile icon={Users} />
                <h2 className="text-2xl font-display font-bold md:text-3xl">{roles.heading}</h2>
              </div>
              <Ticks items={roles.items} />
            </div>
          </div>
        </Section>

        {/* Troubleshooting */}
        <Section labelledBy="trouble-heading">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mb-8 flex items-center gap-3">
              <IconTile icon={LifeBuoy} />
              <h2 id="trouble-heading" className="text-3xl font-display font-bold md:text-4xl">{trouble.heading}</h2>
            </div>
            <ul className="grid gap-4 md:grid-cols-2">
              {trouble.items.map((t) => {
                const Icon = TROUBLE_ICONS[t.id] ?? Check;
                return (
                  <li key={t.id} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                    <IconTile icon={Icon} />
                    <div className="min-w-0">
                      <h3 className="mb-1 font-semibold">{t.title}</h3>
                      <p className="text-sm text-muted-foreground"><Rich text={t.body} /></p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </Section>

        {/* FAQ: native <details>, so every answer is in the server-rendered HTML */}
        <Section alt labelledBy="faq-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="faq-heading" className="mb-8 text-center text-3xl font-display font-bold md:text-4xl">{faq.heading}</h2>
            <div className="divide-y divide-border rounded-2xl bg-card px-6 shadow-lg">
              {faq.items.map((item) => (
                <details key={item.q} className="group py-1">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-base font-medium hover:underline [&::-webkit-details-marker]:hidden">
                    {item.q}
                    <ChevronDown size={16} className="flex-shrink-0 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <p className="pb-4 text-muted-foreground"><Rich text={item.a} /></p>
                </details>
              ))}
            </div>
          </div>
        </Section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-hawk-navy py-20 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
            <Radio className="mx-auto mb-4 text-hawk-orange" size={32} aria-hidden="true" />
            <h2 className="mb-3 text-3xl font-display font-bold md:text-4xl">{cta.heading}</h2>
            <p className="mb-8 text-white/80">{cta.body}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button variant="hero" size="lg" asChild>
                <a href={CLOUDTETHER_URL} target="_blank" rel="noopener noreferrer">
                  {cta.primary}
                  <ExternalLink />
                </a>
              </Button>
              <Button variant="heroOutline" size="lg" asChild>
                <Link to={LIVE_UPLOADING_HELP_PATH}>
                  {cta.secondary}
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LiveUploading;
